import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  gray: '#64748b',
  lightGray: '#f1f5f9',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

// Premium Dashboard Components
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${props => props.background || COLORS.gradient};
  color: ${COLORS.white};
`;

const StatTitle = styled.div`
  font-size: 0.875rem;
  color: ${COLORS.gray};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${COLORS.primary};
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div`
  font-size: 0.875rem;
  color: ${props => props.positive ? COLORS.success : COLORS.danger};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const RecentBookingsContainer = styled.div`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${COLORS.primary};
  margin-bottom: 1.5rem;
`;

const BookingsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 1px solid ${COLORS.lightGray};
  color: ${COLORS.gray};
  font-weight: 500;
  font-size: 0.875rem;
`;

const TableRow = styled.tr`
  &:hover {
    background: ${COLORS.lightGray};
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid ${COLORS.lightGray};
  color: ${COLORS.primary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'confirmed':
        return '#dcfce7';
      case 'pending':
        return '#fef9c3';
      case 'completed':
        return '#e0e7ff';
      default:
        return '#374151';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'confirmed':
        return '#166534';
      case 'pending':
        return '#854d0e';
      case 'completed':
        return '#3730a3';
      default:
        return '#374151';
    }
  }};
`;

const AdminDashboard = () => {
  const [totalBookings, setTotalBookings] = useState(0);
  const [upcomingCheckIns, setUpcomingCheckIns] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch total bookings
        const bookingsCollection = collection(db, 'bookings');
        const bookingsSnapshot = await getDocs(bookingsCollection);
        setTotalBookings(bookingsSnapshot.size);
        
        // Fetch upcoming check-ins (next 7 days)
        const today = new Date();
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 7);
        
        const todayTimestamp = Timestamp.fromDate(today);
        const nextWeekTimestamp = Timestamp.fromDate(nextWeek);
        
        const upcomingQuery = query(
          bookingsCollection,
          where('checkInDate', '>=', todayTimestamp),
          where('checkInDate', '<=', nextWeekTimestamp)
        );
        
        const upcomingSnapshot = await getDocs(upcomingQuery);
        setUpcomingCheckIns(upcomingSnapshot.size);
        
        // Fetch total messages
        const messagesCollection = collection(db, 'contactMessages');
        const messagesSnapshot = await getDocs(messagesCollection);
        setTotalMessages(messagesSnapshot.size);
        
        // Fetch recent bookings
        const recentQuery = query(
          bookingsCollection,
          orderBy('createdAt', 'desc')
        );
        
        const recentSnapshot = await getDocs(recentQuery);
        const bookingsData = recentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          status: doc.data().status || 'pending' // Default status if not set
        })).slice(0, 5);
        
        setRecentBookings(bookingsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    
    fetchDashboardData();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <div>
              <StatTitle>Total Bookings</StatTitle>
              <StatValue>{totalBookings}</StatValue>
              <StatChange positive>
                <span>↑</span>
                12% from last month
              </StatChange>
            </div>
            <StatIcon>📊</StatIcon>
          </StatHeader>
        </StatCard>
        
        <StatCard>
          <StatHeader>
            <div>
              <StatTitle>Upcoming Check-ins</StatTitle>
              <StatValue>{upcomingCheckIns}</StatValue>
              <StatChange positive>
                <span>↑</span>
                Next 7 days
              </StatChange>
            </div>
            <StatIcon background="linear-gradient(135deg, #10b981 0%, #059669 100%)">📅</StatIcon>
          </StatHeader>
        </StatCard>
        
        <StatCard>
          <StatHeader>
            <div>
              <StatTitle>Messages Received</StatTitle>
              <StatValue>{totalMessages}</StatValue>
              <StatChange positive>
                <span>↑</span>
                8% from last week
              </StatChange>
            </div>
            <StatIcon background="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">💬</StatIcon>
          </StatHeader>
        </StatCard>
      </StatsGrid>
      
      <RecentBookingsContainer>
        <SectionTitle>Recent Bookings</SectionTitle>
        
        <BookingsTable>
          <thead>
            <tr>
              <TableHeader>Guest Name</TableHeader>
              <TableHeader>Room Type</TableHeader>
              <TableHeader>Check-in</TableHeader>
              <TableHeader>Check-out</TableHeader>
              <TableHeader>Status</TableHeader>
            </tr>
          </thead>
          <tbody>
            {recentBookings.length > 0 ? (
              recentBookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>{booking.name}</TableCell>
                  <TableCell>{booking.roomType}</TableCell>
                  <TableCell>{formatDate(booking.checkInDate)}</TableCell>
                  <TableCell>{formatDate(booking.checkOutDate)}</TableCell>
                  <TableCell>
                    <StatusBadge status={booking.status || 'pending'}>
                      {(booking.status || 'pending').charAt(0).toUpperCase() + (booking.status || 'pending').slice(1)}
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No bookings found
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </BookingsTable>
      </RecentBookingsContainer>
    </>
  );
};

export default AdminDashboard;
